(() => {
  const ROOT_SELECTOR = '[data-sp-faq-root]';
  const TAB_SELECTOR = '[data-faq-category-tab]';
  const ITEM_SELECTOR = '[data-faq-item]';
  const SUMMARY_CLASS = 'sp-faqs-with-category__summary';
  const BODY_WRAP_CLASS = 'sp-faqs-with-category__body-wrap';

  if (!window.SpFaqsWithCategory) {
    window.SpFaqsWithCategory = {
      instances: new Map(),
      isBound: false,

      init(rootElement) {
        if (!rootElement || this.instances.has(rootElement)) {
          return;
        }

        const tabs = Array.from(rootElement.querySelectorAll(TAB_SELECTOR));
        const items = Array.from(rootElement.querySelectorAll(ITEM_SELECTOR));

        if (!tabs.length || !items.length) {
          return;
        }

        const itemsByCategory = new Map();

        items.forEach((item) => {
          const key = item.dataset.categoryKey || '';
          if (!itemsByCategory.has(key)) {
            itemsByCategory.set(key, []);
          }
          itemsByCategory.get(key).push(item);
        });

        // ── Animation helpers ──────────────────────────────────────────────

        /**
         * Animate close: grid 1fr → 0fr via inline style that overrides the
         * CSS [open] rule, then remove the open attribute once done.
         */
        const closeItem = (item) => {
          if (!item.hasAttribute('open')) return;

          const wrap = item.querySelector(`.${BODY_WRAP_CLASS}`);

          if (!wrap) {
            item.removeAttribute('open');
            return;
          }

          // Cancel any in-progress close listener so we don't double-fire.
          if (item._faqCloseHandler) {
            wrap.removeEventListener('transitionend', item._faqCloseHandler);
          }

          // Setting inline style overrides the CSS [open] rule (1fr → 0fr).
          wrap.style.gridTemplateRows = '0fr';

          item._faqCloseHandler = (e) => {
            if (e.propertyName !== 'grid-template-rows') return;
            item.removeAttribute('open');
            wrap.style.gridTemplateRows = '';
            delete item._faqCloseHandler;
          };

          wrap.addEventListener('transitionend', item._faqCloseHandler, { once: true });
        };

        /**
         * Open instantly (set open attribute → CSS [open] rule kicks in →
         * grid-template-rows transitions 0fr → 1fr automatically).
         * If a close animation was in progress, cancel it first.
         */
        const openItem = (item) => {
          if (item.hasAttribute('open')) return;

          const wrap = item.querySelector(`.${BODY_WRAP_CLASS}`);

          if (wrap && item._faqCloseHandler) {
            wrap.removeEventListener('transitionend', item._faqCloseHandler);
            wrap.style.gridTemplateRows = '';
            delete item._faqCloseHandler;
          }

          item.setAttribute('open', '');
          // CSS [open] rule now applies: grid-template-rows transitions 0fr → 1fr.
        };

        // ── Category tab logic ─────────────────────────────────────────────

        const updateActiveTab = (activeKey) => {
          tabs.forEach((tab) => {
            tab.setAttribute(
              'aria-pressed',
              tab.dataset.categoryKey === activeKey ? 'true' : 'false'
            );
          });
        };

        const toggleItemsByCategory = (activeKey) => {
          items.forEach((item) => {
            const isMatch = item.dataset.categoryKey === activeKey;
            item.hidden = !isMatch;

            if (!isMatch) {
              // Cancel any in-progress animation and force-close immediately.
              const wrap = item.querySelector(`.${BODY_WRAP_CLASS}`);
              if (wrap && item._faqCloseHandler) {
                wrap.removeEventListener('transitionend', item._faqCloseHandler);
                wrap.style.gridTemplateRows = '';
                delete item._faqCloseHandler;
              }
              item.removeAttribute('open');
            }
          });
        };

        const getValidCategoryKey = (requestedKey) => {
          if (requestedKey && itemsByCategory.has(requestedKey)) {
            return requestedKey;
          }
          return tabs[0] ? tabs[0].dataset.categoryKey || '' : '';
        };

        const activateCategory = (requestedKey) => {
          const validKey = getValidCategoryKey(requestedKey);
          if (!validKey) return;
          updateActiveTab(validKey);
          toggleItemsByCategory(validKey);
        };

        // ── Event handlers ─────────────────────────────────────────────────

        const onTabClick = (event) => {
          activateCategory(event.currentTarget.dataset.categoryKey || '');
        };

        /**
         * Intercept the default <details> toggle so we can drive the animation
         * ourselves instead of letting the browser snap open/closed.
         */
        const onSummaryClick = (event) => {
          event.preventDefault();

          const item = event.currentTarget.closest(ITEM_SELECTOR);
          if (!item) return;

          if (item.hasAttribute('open')) {
            closeItem(item);
          } else {
            // Animate-close any other open items in the same section first.
            items.forEach((other) => {
              if (other !== item && !other.hidden && other.hasAttribute('open')) {
                closeItem(other);
              }
            });
            openItem(item);
          }
        };

        // ── Attach listeners ───────────────────────────────────────────────

        const cleanupHandlers = [];

        tabs.forEach((tab) => {
          tab.addEventListener('click', onTabClick);
          cleanupHandlers.push(() => tab.removeEventListener('click', onTabClick));
        });

        items.forEach((item) => {
          const summary = item.querySelector(`.${SUMMARY_CLASS}`);
          if (!summary) return;
          summary.addEventListener('click', onSummaryClick);
          cleanupHandlers.push(() => summary.removeEventListener('click', onSummaryClick));
        });

        const defaultCategory = rootElement.dataset.defaultCategory || '';
        activateCategory(defaultCategory);

        this.instances.set(rootElement, () => {
          cleanupHandlers.forEach((cleanup) => cleanup());
        });
      },

      destroy(rootElement) {
        if (!rootElement) return;
        const cleanup = this.instances.get(rootElement);
        if (cleanup) cleanup();
        this.instances.delete(rootElement);
      },

      initWithin(container) {
        if (!container) return;
        const roots = container.matches?.(ROOT_SELECTOR)
          ? [container]
          : Array.from(container.querySelectorAll(ROOT_SELECTOR));
        roots.forEach((root) => this.init(root));
      },

      destroyWithin(container) {
        if (!container) return;
        const roots = container.matches?.(ROOT_SELECTOR)
          ? [container]
          : Array.from(container.querySelectorAll(ROOT_SELECTOR));
        roots.forEach((root) => this.destroy(root));
      },

      bindSectionEvents() {
        if (this.isBound) return;

        document.addEventListener('shopify:section:load', (event) => {
          this.initWithin(event.target);
        });

        document.addEventListener('shopify:section:unload', (event) => {
          this.destroyWithin(event.target);
        });

        this.isBound = true;
      },
    };
  }

  window.SpFaqsWithCategory.bindSectionEvents();
  window.SpFaqsWithCategory.initWithin(document);
})();
